// src/hooks/useModels.js
import { useEffect, useState } from 'react';
import { modelService } from '../api/modelService';

/**
 * Fetches the live list of LLMs once per component mount.
 * Returns [models, loading, error], where models is an array of { id: string, name: string }.
 * The list is sorted alphabetically by name.
 */
export const useModels = (fallback = []) => {
  // Store an array of model objects { id, name }
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await modelService.getAvailableModels();
      if (ignore) return;

      let processedModels = [];

      if (res.isError) {
        console.warn('Falling back to hard-coded MODEL_OPTIONS:', res.message);
        setError(res.message);
        // Convert fallback strings to the desired object structure
        processedModels = fallback.map(id => ({ id: id, name: id }));

      } else if (Array.isArray(res)) {
        // Assuming res is already an array of model objects with 'id' and 'name'
        // Or adapt if the structure is different (e.g., just strings)
        processedModels = res
          .filter(m => m && m.id) // Ensure valid model objects
          .map(m => ({
            id: m.id,
            name: m.name || m.id // Use name, fallback to id
          }));

      } else if (res && typeof res === 'object') {
         // Handle cases where the API might return an object like { data: [...] }
         // Or if it returns an object keyed by model ID: { "model-id": { details... } }
         let modelsArray = [];
         if (Array.isArray(res.data)) {
            modelsArray = res.data; // Common pattern if wrapped in 'data'
         } else if (!Array.isArray(res)) {
            // Assuming keys are model IDs and values are details
             modelsArray = Object.entries(res).map(([id, details]) => ({
                id: id,
                ...details // Spread details like name, description etc.
             }));
         }

         processedModels = modelsArray
            .filter(m => m && m.id)
            .map(m => ({
                id: m.id,
                name: m.name || m.id // Use name, fallback to id
            }));

      } else {
         // Unexpected format, use fallback
         console.warn('Unexpected format for available models, using fallback.');
         processedModels = fallback.map(id => ({ id: id, name: id }));
      }

      // Sort models alphabetically by name for consistent display
      processedModels.sort((a, b) => a.name.localeCompare(b.name));

      setModels(processedModels);
      setLoading(false);
    })();

    return () => { ignore = true; };
    // Ensure fallback is stable or memoized if it's an object/array defined in parent
  }, [JSON.stringify(fallback)]); // Use JSON.stringify for array/object dependency

  // Return the array of { id, name } objects
  return [models, loading, error];
};